import { afterNextRender, DestroyRef, Directive, DoCheck, ElementRef, HostListener, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgControl } from '@angular/forms';

import { IBAN_MAX_LENGTH } from 'app/constants';

/**
 * Use on **editable** `<input>` elements bound to a form control
 * (`formControlName` / `[formControl]`).
 *
 * Formats the IBAN visually into 4-character blocks as the user types, while the
 * underlying control always holds the raw, unformatted value (spaces stripped) —
 * so the value sent to the backend is never affected by the display formatting.
 * Also filters keystrokes to alphanumeric-only and manages cursor position.
 *
 * For **read-only, display-only** IBANs use the `stbyIbanFormat` pipe
 * instead — it reuses the same `formatIban()` grouping logic without the
 * overhead of directive lifecycle hooks, DOM mutation, or keydown handling.
 */
@Directive({
    selector: 'input[stbyIbanInput]',
})
export class IbanInputDirective implements DoCheck {
    private readonly el = inject<ElementRef<HTMLInputElement>>(ElementRef);
    private readonly ngControl = inject(NgControl, { optional: true });
    private readonly destroyRef = inject(DestroyRef);
    private lastRenderedValue = '';

    constructor() {
        afterNextRender(() => {
            const input = this.el.nativeElement;
            input.setAttribute('autocomplete', 'off');
            input.setAttribute('spellcheck', 'false');

            const ctrl = this.ngControl?.control;
            if (!ctrl) return;

            // Format when value is set externally (patchValue, reset, initial API load).
            ctrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value: string) => {
                const raw = normalise(value ?? '');
                const formatted = formatIban(raw);
                if (input.value !== formatted) {
                    input.value = formatted;
                }
                if (value !== raw) {
                    ctrl.setValue(raw, { emitEvent: false });
                }
                this.lastRenderedValue = input.value;
            });

            // Format value already present at first render
            if (input.value) {
                input.value = formatIban(normalise(input.value));
            }
            this.lastRenderedValue = input.value;
        });
    }

    ngDoCheck(): void {
        // Runs regardless of ngControl so DOM value changes written by the
        // ControlValueAccessor with `{ emitEvent: false }` (which bypass
        // valueChanges) still get formatted.
        const input = this.el.nativeElement;
        if (input.value === this.lastRenderedValue) {
            return;
        }

        const formatted = formatIban(normalise(input.value));
        if (formatted !== input.value) {
            input.value = formatted;
        }

        this.lastRenderedValue = input.value;
    }

    @HostListener('input')
    onInput(): void {
        const input = this.el.nativeElement;
        const cursorPos = input.selectionStart ?? 0;
        const rawCharCountBeforeCursor = normalise(input.value.slice(0, cursorPos)).length;

        const raw = normalise(input.value);
        const formatted = formatIban(raw);

        input.value = formatted;
        const newCursor = cursorPosInFormatted(formatted, rawCharCountBeforeCursor);
        input.setSelectionRange(newCursor, newCursor);
        this.lastRenderedValue = formatted;

        queueMicrotask(() => {
            const control = this.ngControl?.control;
            if (!control || control.value === raw) {
                return;
            }
            control.setValue(raw, { emitEvent: false });
        });
    }

    @HostListener('keydown', ['$event'])
    onKeyDown(event: KeyboardEvent): void {
        if (isAllowedKey(event)) return;
        if (/^[A-Za-z0-9]$/.test(event.key)) return;
        event.preventDefault();
    }
}

function normalise(value: string): string {
    return (value ?? '')
        .replace(/[^A-Za-z0-9]/g, '')
        .toUpperCase()
        .slice(0, IBAN_MAX_LENGTH);
}

export function formatIban(raw: string): string {
    return raw.replace(/(.{4})(?=.)/g, '$1 ');
}

function cursorPosInFormatted(formatted: string, rawCharCount: number): number {
    if (rawCharCount === 0) return 0;
    let count = 0;
    for (let i = 0; i < formatted.length; i++) {
        if (formatted[i] !== ' ') {
            count++;
            if (count === rawCharCount) return i + 1;
        }
    }
    return formatted.length;
}

function isAllowedKey(event: KeyboardEvent): boolean {
    const navigationKeys = ['ArrowLeft', 'ArrowRight', 'Backspace', 'Delete', 'Tab', 'Home', 'End', 'Enter'];
    if (navigationKeys.includes(event.key)) return true;
    const isCtrlOrCmd = event.ctrlKey || event.metaKey;
    return isCtrlOrCmd && ['a', 'c', 'v', 'x', 'z'].includes(event.key.toLowerCase());
}
